import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import logo from '../assets/readlinker-logo.svg';
import logoSmall from '../assets/images/readLinker_mini_logo_128.png';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Backdrop from '@mui/material/Backdrop';
import Switch from '@mui/material/Switch';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { getUserProfileImageByuserSubCF } from '../utils/userProfileApi';
import { useThemeMode } from '../context/ThemeModeContext';
import AccountSwitcherMenu from './AccountSwitcherMenu';

const GraphPageHeader: React.FC = () => {
  const { isLoggedIn, userSub, loggedInAccounts } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [accountMenuAnchorEl, setAccountMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isSearchActive, setIsSearchActive] = useState<boolean>(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { mode, toggleMode } = useThemeMode();

  // アカウント切替でuserSubが変わるたびに追従させる必要があるため、
  // アカウント登録済みの一覧（cognito:username由来で確実に正しい）から都度導出する。
  // 以前は isLoggedIn だけを見て一度だけセッションを取得していたため、
  // 切り替えてもヘッダーのアバター画像だけ古いアカウントのまま残るバグがあった。
  const username = loggedInAccounts.find((a) => a.sub === userSub)?.username ?? null;

  useEffect(() => {
    if (!isLoggedIn || !userSub) {
      setProfileImage(null);
      return;
    }
    getUserProfileImageByuserSubCF(userSub)
      .then(img => setProfileImage(img || null))
      .catch(() => setProfileImage(null));
  }, [isLoggedIn, userSub]);

  const openNewTab = (path: string) => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: 'rgb(35,48,72)',
          boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
          zIndex: theme => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: 64, display: 'flex', justifyContent: 'space-between' }}>
          <a href="/" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src={isSmallScreen ? logoSmall : logo}
              alt="ReadLinker Logo"
              style={{ height: isSmallScreen ? 28 : 36, display: 'block' }}
            />
          </a>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isLoggedIn && (
              <>
                {!isSmallScreen && (
                  <Button color="inherit" onClick={() => openNewTab('/bookshelf')}>
                    本棚
                  </Button>
                )}

                {profileImage && (
                  <img
                    src={profileImage}
                    alt="profile"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      border: '1px solid rgba(255,255,255,0.4)',
                      cursor: 'pointer',
                    }}
                    onClick={(e) => setAccountMenuAnchorEl(e.currentTarget)}
                  />
                )}
              </>
            )}

            {!isLoggedIn && !isSmallScreen && (
              <Button color="inherit" onClick={() => openNewTab('/login')}>
                ログイン
              </Button>
            )}

            <IconButton color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)}>
              <MenuIcon />
            </IconButton>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              {isLoggedIn && <MenuItem onClick={() => { openNewTab('/bookshelf'); setAnchorEl(null); }}>本棚</MenuItem>}
              {isLoggedIn && <MenuItem onClick={() => { openNewTab('/bibliography'); setAnchorEl(null); }}>書誌管理</MenuItem>}
              {isLoggedIn && <MenuItem onClick={() => { openNewTab('/shelves/manage'); setAnchorEl(null); }}>本棚編集</MenuItem>}
              {isLoggedIn && <MenuItem onClick={() => { openNewTab('/favBooks'); setAnchorEl(null); }}>お気に入り</MenuItem>}
              {isLoggedIn && <MenuItem onClick={() => { openNewTab('/mylist'); setAnchorEl(null); }}>リスト</MenuItem>}
              {isLoggedIn && <MenuItem onClick={() => { openNewTab('/highlights'); setAnchorEl(null); }}>抜粋・メモ</MenuItem>}
              {isLoggedIn && username && <MenuItem onClick={() => { openNewTab(`/${username}`); setAnchorEl(null); }}>マイプロフィール</MenuItem>}
              <Divider />
              <MenuItem onClick={() => { openNewTab('/about-readlinker'); setAnchorEl(null); }}>ReadLinkerについて</MenuItem>
              {!isLoggedIn && <MenuItem onClick={() => { openNewTab('/login'); setAnchorEl(null); }}>ログイン</MenuItem>}
              <MenuItem
                onClick={() => { toggleMode(); setAnchorEl(null); }}
                sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {mode === 'dark' ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
                  {mode === 'dark' ? 'ダーク' : 'ライト'}
                </Box>
                <Switch
                  size="small"
                  checked={mode === 'light'}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => { toggleMode(); setAnchorEl(null); }}
                />
              </MenuItem>
            </Menu>

            <AccountSwitcherMenu anchorEl={accountMenuAnchorEl} onClose={() => setAccountMenuAnchorEl(null)} />
          </Box>
        </Toolbar>
      </AppBar>

      <Backdrop
        sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer, backgroundColor: 'rgba(0,0,0,0.5)' }}
        open={isSearchActive}
        onClick={() => setIsSearchActive(false)}
      />
    </>
  );
};

export default GraphPageHeader;
